import { useEffect } from 'react'
import CloseIcon from '@mui/icons-material/Close';
import FeedIcon from '@mui/icons-material/Feed';
import { FlexibleTextArea, Input } from '../../../../../common/Input';
import { StatusBadge } from '../../../../../common/Badge';
import { DepartmentIdSelect, PositionIdSelect } from '../../../../reusables/selection';
import { WorkflowStatus } from '../../../../../utils/enums';
import { Button } from '../../../../../common/APICommon';

export const AddUpdateModal = ({
  visible,
  onClose,
  onSave,
  modalName,
  userData,
  form, setForm,
  selectedData,
  showGeneralMessage // Replacement for setFailMessage
}) => {

  const isEdit = modalName?.toLowerCase().includes("edit");

  useEffect(() => {
    if (visible && userData?.id && !isEdit) {
      setForm((prev) => ({ ...prev, requesterId: userData.id }));
    }
  }, [visible, userData, setForm, isEdit]);


  useEffect(() => {
    if (visible && isEdit && selectedData) {
      setForm({
        requesterId: selectedData.requesterId || "",
        positionId: selectedData.positionId || "",
        departmentId: selectedData.departmentId || "",
        numberOfVacancies: selectedData.numberOfVacancies || "",
        requesterNotes: selectedData.requesterNotes || "",
        qualification: selectedData.qualification || "",
        status: selectedData.status || "",
        targetStartDate: selectedData.targetStartDate ? selectedData.targetStartDate.split('T')[0] : "",
        approvedDate: selectedData.approvedDate || null,
        requestedDate: selectedData.requestedDate ? selectedData.requestedDate.split('T')[0] : "",
        completedDate: selectedData.completedDate || null,
        isActive: selectedData.isActive !== undefined ? selectedData.isActive : true,
      });
    }

  }, [visible, isEdit, selectedData, setForm]);


  if (!visible) return null;

  const handleSubmit = async () => {
    const requiredFields = [
      { key: "positionId", label: "Position" },
      { key: "departmentId", label: "Department" },
      { key: "numberOfVacancies", label: "Number of Vacancies" },
      { key: "targetStartDate", label: "Target Start Date" },
      { key: "qualification", label: "Qualification" }
    ];

    const missingField = requiredFields.find((f) => !form[f.key]);

    if (missingField) {
      showGeneralMessage(`Please fill up the required field: ${missingField.label}`, false);
      return;
    }

    const vacancies = Number(form.numberOfVacancies);
    if (isNaN(vacancies) || vacancies <= 0 || !Number.isInteger(vacancies)) {
      showGeneralMessage("Number of Vacancies must be a positive whole number.", false);
      return;
    }

    await onSave(form);
    closeModal();
  };


  const closeModal = () => {
    onClose();
    if (!isEdit) {
      setForm({
        requesterId: "", positionId: "", departmentId: "", numberOfVacancies: "", requesterNotes: "",
        qualification: "", status: "", targetStartDate: null, approvedDate: null, requestedDate: null,
        completedDate: null, isActive: true,
      });
    }
  };

  return (
    <div role="dialog" className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
        <div className="bg-gray-50 w-full md:w-[90%] lg:w-[80%] rounded-lg shadow-xl h-[90vh] flex flex-col relative">
          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <FeedIcon fontSize="large" className="text-blue-400" />
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>
            <button onClick={closeModal} className="text-gray-400 hover:text-gray-700 p-1 bg-transparent border-none"><CloseIcon fontSize="medium" /></button>
          </div>

          <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-3 overflow-y-auto flex-grow">
            <div className="flex flex-col h-full">
              <div className="mb-6 border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
                <div className="py-1 border-b border-gray-300"><h3 className="font-medium text-gray-700">Requester Information</h3></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 pb-3 pt-2">
                  <Input label="Requester Position" value={userData?.position ?? "Position..."} isDisabled />
                  <Input label="Requested By" value={userData?.fullName ?? "Requester Name..."} isDisabled />
                  {isEdit && selectedData?.status && <Input label="Current Status" value={selectedData.status} isDisabled />}
                </div>
              </div>

              <div className="mb-6 border border-gray-300 rounded-lg bg-white px-4 shadow-sm flex flex-col flex-1">
                <div className="flex justify-between items-center py-1 border-b border-gray-300">
                  <h3 className="font-medium text-gray-700">MRF Details</h3>
                  {isEdit && selectedData?.status && <StatusBadge status={selectedData.status} />}
                </div>
                <div className="pb-3 flex flex-col flex-1 pt-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                    <PositionIdSelect label="Position" isRequired positionId={form.positionId} setPositionId={(v) => setForm({ ...form, positionId: Number(v) })} isDisabled={isEdit && selectedData?.status !== WorkflowStatus.PENDING} />
                    <DepartmentIdSelect label="Department" isRequired departmentId={form.departmentId} setDepartmentId={(v) => setForm({ ...form, departmentId: Number(v) })} isDisabled={isEdit && selectedData?.status !== WorkflowStatus.PENDING} />
                    <Input label="Number of Vacancies" value={form.numberOfVacancies} placeholder='0' onChange={(e) => setForm({ ...form, numberOfVacancies: Number(e.target.value) })} isRequired isDisabled={isEdit && selectedData?.status !== WorkflowStatus.PENDING} />
                    <Input label="Target Start Date" type="date" value={form.targetStartDate} onChange={(e) => setForm({ ...form, targetStartDate: e.target.value })} isRequired isDisabled={isEdit && selectedData?.status !== WorkflowStatus.PENDING} />
                  </div>
                  <FlexibleTextArea label="Requester Notes" placeholder="Your notes here..." value={form.requesterNotes} onChange={(e) => setForm({ ...form, requesterNotes: e.target.value })} className="mt-3 flex-1" isDisabled={isEdit && selectedData?.status !== WorkflowStatus.PENDING} />
                </div>
              </div>
            </div>

            <div className="bg-white flex flex-col h-full rounded p-3 shadow-sm border border-gray-300">
              <h3 className="font-medium text-gray-700 py-1 border-b">Qualification Details <span className="text-red-500 ml-1">*</span></h3>
              <FlexibleTextArea isRequired isDisabled={isEdit && selectedData?.status !== WorkflowStatus.PENDING} value={form.qualification} placeholder="Enter Qualification Details..." onChange={(e) => setForm({ ...form, qualification: e.target.value })} className="flex-1 mt-3" />
            </div>
          </div>

          <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white shadow-sm">
            <Button name={"Cancel"} className={"bg-gray-400 hover:bg-gray-500 border-none !px-4 !py-3 mb-2"} onClick={closeModal} />
            <Button name={isEdit ? "Update" : "Send"} className={"bg-blue-500 hover:bg-blue-600 border-none !px-4 !py-3 mb-2"} onClick={handleSubmit} disabled={(selectedData?.status === WorkflowStatus.CANCELLED || selectedData?.status === WorkflowStatus.COMPLETED)} />
          </div>
        </div>
      </div>
  );
};